import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/api/gallery`);
        setPhotos(res.data.photos || []);
        setVideos(res.data.videos || []);
      } catch {
        // Fallback silently
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const handleImageError = (id) => {
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  const getVideoEmbedUrl = (video) => {
    if (video.videoSource === 'gdrive') {
      return `https://drive.google.com/file/d/${video.videoId}/preview`;
    }
    return `https://www.youtube.com/embed/${video.videoId}`;
  };

  const validPhotos = photos.filter(photo => !imageErrors[photo.id]);

  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif font-bold text-5xl text-primary mb-4">Gallery</h1>
          <p className="text-lg text-muted-foreground">
            समाज के कार्यक्रमों और आयोजनों की झलकियां
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          {[
            { key: 'all', label: 'All / सभी' },
            { key: 'videos', label: 'Videos / वीडियो' },
            { key: 'photos', label: 'Photos / फोटो' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-2 rounded-full font-semibold transition-all ${
                activeTab === tab.key
                  ? 'bg-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Videos Section */}
        {(activeTab === 'all' || activeTab === 'videos') && videos.length > 0 && (
          <section className="mb-12">
            <h2 className="font-serif font-bold text-2xl text-secondary mb-6 flex items-center gap-2">
              <Play className="w-6 h-6" />
              Videos / वीडियो
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.map((video) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-xl overflow-hidden shadow-card"
                >
                  <div className="relative pb-[56.25%] h-0">
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={getVideoEmbedUrl(video)}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif font-bold text-lg text-foreground">{video.title}</h3>
                    {video.titleTranslated && (
                      <h4 className="font-serif text-base text-foreground/70">{video.titleTranslated}</h4>
                    )}
                    <p className="text-sm text-muted-foreground">{video.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Photos Section */}
        {(activeTab === 'all' || activeTab === 'photos') && (
          <section>
            <h2 className="font-serif font-bold text-2xl text-secondary mb-6 flex items-center gap-2">
              <ImageIcon className="w-6 h-6" />
              Photos / फोटो ({validPhotos.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {validPhotos.map((photo, index) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedImage(photo)}
                >
                  <div className="bg-card rounded-xl overflow-hidden shadow-card hover:shadow-floating transition-shadow">
                    <div className="aspect-square overflow-hidden bg-muted">
                      <img
                        src={resolveUrl(photo.url)}
                        alt={photo.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={() => handleImageError(photo.id)}
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-serif font-bold text-sm text-foreground truncate">
                        {photo.title}
                      </h3>
                      {photo.titleTranslated && (
                        <p className="font-serif text-xs text-foreground/70 truncate">
                          {photo.titleTranslated}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground truncate">
                        {photo.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {validPhotos.length === 0 && videos.length === 0 && (
          <div className="text-center py-12 bg-muted/30 rounded-xl">
            <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No gallery items to display</p>
          </div>
        )}

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-secondary transition-colors z-10"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <div className="max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <img
                src={resolveUrl(selectedImage.url)}
                alt={selectedImage.title}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="text-center mt-4">
                <h3 className="font-serif font-bold text-xl text-white">{selectedImage.title}</h3>
                {selectedImage.titleTranslated && (
                  <h4 className="font-serif text-lg text-white/80">{selectedImage.titleTranslated}</h4>
                )}
                <p className="text-white/70">{selectedImage.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryPage;
